import type { ReactElement, SVGProps } from "react";

export type AdminToolIconId =
  | "design-system"
  | "system-catalog"
  | "overlays"
  | "tiles"
  | "compose"
  | "users";

type IconProps = SVGProps<SVGSVGElement> & { title?: string };

const stroke = {
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  viewBox: "0 0 24 24",
};

function DesignSystemIcon(props: IconProps) {
  const { title, className, ...rest } = props;
  return (
    <svg {...stroke} className={className} aria-hidden={title ? undefined : true} {...rest}>
      {title ? <title>{title}</title> : null}
      <path d="M4 21v-7" />
      <path d="M4 10V3" />
      <path d="M12 21v-9" />
      <path d="M12 8V3" />
      <path d="M20 21v-5" />
      <path d="M20 12V3" />
      <path d="M2 14h4" />
      <path d="M10 8h4" />
      <path d="M18 16h4" />
    </svg>
  );
}

function SystemCatalogIcon(props: IconProps) {
  const { title, className, ...rest } = props;
  return (
    <svg {...stroke} className={className} aria-hidden={title ? undefined : true} {...rest}>
      {title ? <title>{title}</title> : null}
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
      <path d="M2 10h20" />
    </svg>
  );
}

function OverlaysIcon(props: IconProps) {
  const { title, className, ...rest } = props;
  return (
    <svg {...stroke} className={className} aria-hidden={title ? undefined : true} {...rest}>
      {title ? <title>{title}</title> : null}
      <rect x="8" y="8" width="13" height="13" rx="2" />
      <path d="M3 11a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    </svg>
  );
}

function TilesIcon(props: IconProps) {
  const { title, className, ...rest } = props;
  return (
    <svg {...stroke} className={className} aria-hidden={title ? undefined : true} {...rest}>
      {title ? <title>{title}</title> : null}
      <rect width="7" height="7" x="3" y="3" rx="1" />
      <rect width="7" height="7" x="14" y="3" rx="1" />
      <rect width="7" height="7" x="14" y="14" rx="1" />
      <rect width="7" height="7" x="3" y="14" rx="1" />
    </svg>
  );
}

function ComposeIcon(props: IconProps) {
  const { title, className, ...rest } = props;
  return (
    <svg {...stroke} className={className} aria-hidden={title ? undefined : true} {...rest}>
      {title ? <title>{title}</title> : null}
      <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
      <path d="m22 17.65-9.07 4.13a2 2 0 0 1-1.66 0L2 17.65" />
      <path d="m22 12.65-9.07 4.13a2 2 0 0 1-1.66 0L2 12.65" />
    </svg>
  );
}

function UsersIcon(props: IconProps) {
  const { title, className, ...rest } = props;
  return (
    <svg {...stroke} className={className} aria-hidden={title ? undefined : true} {...rest}>
      {title ? <title>{title}</title> : null}
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

const GLYPHS: Record<AdminToolIconId, (p: IconProps) => ReactElement> = {
  "design-system": DesignSystemIcon,
  "system-catalog": SystemCatalogIcon,
  overlays: OverlaysIcon,
  tiles: TilesIcon,
  compose: ComposeIcon,
  users: UsersIcon,
};

export function AdminToolIcon({
  id,
  className,
  title,
  ...rest
}: IconProps & { id: AdminToolIconId }) {
  const Glyph = GLYPHS[id];
  return <Glyph className={className} title={title} {...rest} />;
}

/** Sidebar link back to the developer hub landing cards. */
export function DeveloperHubHomeIcon(props: IconProps) {
  const { title, className, ...rest } = props;
  return (
    <svg {...stroke} className={className} aria-hidden={title ? undefined : true} {...rest}>
      {title ? <title>{title}</title> : null}
      <rect width="7" height="9" x="3" y="3" rx="1" />
      <rect width="7" height="5" x="14" y="3" rx="1" />
      <rect width="7" height="9" x="14" y="12" rx="1" />
      <rect width="7" height="7" x="3" y="14" rx="1" />
    </svg>
  );
}
